/**
 * Celebration — the hand-drawn flourish when the puzzle is solved.
 *
 * Two layered gestures:
 *   1. A torn corner peels off the top-right of the page — small triangle of
 *      paper lifting away (CSS transform + opacity).
 *   2. Above the grid, a hand-scribbled "Done!" appears in cursive, drawn
 *      via SVG stroke-dasharray over ~900ms. A small drawn star next to it
 *      sketches in afterward (~300ms).
 *
 * Sound is driven by the parent at puzzle-solve time. This component is
 * purely visual.
 */
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { formatElapsed } from '@/games/sudoku/engine';

import { FLOURISH_DRAW_MS } from './motion';
import { sound } from './sound';

import './Celebration.css';

export interface CelebrationProps {
  active: boolean;
  elapsedSec: number;
}

export function Celebration({ active, elapsedSec }: CelebrationProps): React.JSX.Element | null {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<'draw' | 'rest'>('draw');

  useEffect(() => {
    if (!active) return;
    // Sound choreography: pencil-scratches building → tap-tap → chime → close.
    const playSeq = (): void => {
      if (reduced) {
        sound.play('chime');
        sound.play('notebookClose');
        return;
      }
      // light scratches
      sound.play('pencilScratch');
      window.setTimeout(() => sound.play('pencilScratch'), 140);
      window.setTimeout(() => sound.play('pencilScratchLong'), 320);
      // pencil tap-tap
      window.setTimeout(() => sound.play('pencilTap'), 700);
      window.setTimeout(() => sound.play('pencilTap'), 820);
      // chime as flourish finishes
      window.setTimeout(() => sound.play('chime'), 950);
      // notebook close after the rest
      window.setTimeout(() => sound.play('notebookClose'), 1450);
    };
    playSeq();
    const t = window.setTimeout(() => setPhase('rest'), reduced ? 30 : FLOURISH_DRAW_MS + 200);
    return () => window.clearTimeout(t);
  }, [active, reduced]);

  if (!active) return null;

  return (
    <motion.div
      className="nb-celebrate"
      aria-hidden="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="nb-celebrate__corner" data-phase={phase}>
        <CornerTear />
      </div>

      <div className="nb-celebrate__flourish">
        <FlourishWord />
        <Star delay={reduced ? 0 : 800} />
        <Underline delay={reduced ? 0 : 950} />
      </div>

      <div className="nb-celebrate__time">
        <span className="nb-celebrate__time-label">finished</span>
        <span className="nb-celebrate__time-value">{formatElapsed(elapsedSec)}</span>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// SVG building blocks
// ---------------------------------------------------------------------------

function FlourishWord() {
  // "Done!" rendered as cursive text in our hand-drawn font. We animate it
  // in via clip-path so it appears to write itself left-to-right.
  return (
    <svg viewBox="0 0 480 200" className="nb-celebrate__svg" aria-hidden="true">
      <defs>
        <clipPath id="nb-write-clip">
          <rect x="0" y="0" width="0" height="200">
            <animate
              attributeName="width"
              from="0"
              to="480"
              dur="1.05s"
              fill="freeze"
              begin="0.1s"
              calcMode="spline"
              keySplines="0.33 1 0.68 1"
            />
          </rect>
        </clipPath>
      </defs>
      <g clipPath="url(#nb-write-clip)">
        <text
          x="20"
          y="150"
          className="nb-flourish-text nb-flourish-text--faint"
          aria-hidden="true"
        >
          Done!
        </text>
        <text
          x="22"
          y="148"
          className="nb-flourish-text"
          aria-hidden="true"
        >
          Done!
        </text>
      </g>
    </svg>
  );
}

function Star({ delay }: { delay: number }) {
  return (
    <svg viewBox="0 0 80 80" className="nb-celebrate__star" style={{ animationDelay: `${delay}ms` }} aria-hidden="true">
      <path
        d="M40,8 L48,32 L72,32 L52,48 L60,72 L40,58 L20,72 L28,48 L8,32 L32,32 Z"
        fill="none"
        className="nb-star-path"
      />
    </svg>
  );
}

function Underline({ delay }: { delay: number }) {
  return (
    <svg viewBox="0 0 480 30" className="nb-celebrate__underline" style={{ animationDelay: `${delay}ms` }} aria-hidden="true">
      <path
        d="M10,15 C80,4 150,24 230,12 C310,2 380,22 470,10"
        fill="none"
        className="nb-underline-path"
      />
      <path
        d="M14,22 C84,12 160,28 240,18 C320,8 390,26 466,16"
        fill="none"
        className="nb-underline-path nb-underline-path--faint"
      />
    </svg>
  );
}

function CornerTear() {
  return (
    <svg viewBox="0 0 200 200" className="nb-corner" aria-hidden="true">
      <defs>
        <linearGradient id="nb-corner-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(120, 95, 60, 0.0)" />
          <stop offset="60%" stopColor="rgba(120, 95, 60, 0.15)" />
          <stop offset="100%" stopColor="rgba(120, 95, 60, 0.35)" />
        </linearGradient>
      </defs>
      {/* Lifted-corner shadow */}
      <path
        d="M200,0 L60,0 Q92,46 118,76 Q150,112 200,140 Z"
        fill="url(#nb-corner-grad)"
        opacity="0.55"
      />
      <path
        d="M200,0 L80,0 Q108,42 130,72 Q156,104 200,128 Z"
        className="nb-corner__paper"
      />
      <path
        d="M80,0 Q108,42 130,72 Q156,104 200,128"
        className="nb-corner__fold"
        fill="none"
      />
    </svg>
  );
}
